/** 사용량 → 웹 서비스 (`/analytics/web`): every web service on one table.
 *
 *  The numbers are read from Cloudflare when the page asks and kept five
 *  minutes on the server, so nothing here is "collected". A service is a row
 *  in settings → 웹 서비스; this page lists the switched-on ones. */

import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, type WebUsageSummary } from '@/api/client'
import { formatRelative, fromUnixMs } from '@/lib/time'
import { qk } from '@/lib/queries'
import { DataTable, Disclosure, EmptyState, FilterBar, Notice, PageHeader, StatTile, type Column } from '@/ui'
import { SessionBars } from '../charts'
import { fmtDuration, fmtNum, LIST_DAYS, rangeLabel, rangeTitle, windowLabel } from '../format'
import { useWindowDays } from '../params'
import { WindowPicker } from '../pickers'
import { ScopePicker, UsageTabs, useInternal } from './shared'

export default function WebUsageList() {
  const [days, setDays] = useWindowDays(LIST_DAYS)
  const [internal, setInternal] = useInternal()
  const list = useQuery({
    queryKey: qk.webUsageList(days, internal),
    queryFn: () => api.webUsageList(days, internal),
    placeholderData: keepPreviousData,
  })

  const items = list.data?.items ?? []
  const configured = list.data?.configured ?? true
  const sessions = items.reduce((n, s) => n + s.sessions, 0)
  const timeMs = items.reduce((n, s) => n + s.time_ms, 0)
  const last = items.reduce((n, s) => Math.max(n, s.last_event_at), 0)
  const buckets = items[0]?.daily ?? []
  const q = new URLSearchParams()
  q.set('days', String(days))
  if (internal) q.set('internal', '1')
  const href = (s: WebUsageSummary) => `/analytics/web/${encodeURIComponent(s.id)}?${q}`

  const columns: Column<WebUsageSummary>[] = [
    {
      key: 'service',
      header: '서비스',
      card: 'title',
      cell: (s) => (
        <Link to={href(s)} className="block min-w-0 hover:text-accent">
          <span className="block truncate font-medium">{s.name}</span>
          <span className="block truncate font-mono text-micro text-muted-foreground">{s.url.replace(/^https:\/\//, '') || s.id}</span>
        </Link>
      ),
    },
    {
      key: 'trend',
      header: <span title="기간 안 UTC 일별 세션. 가장 새 막대가 오른쪽이다.">추이</span>,
      card: 'line',
      cell: (s) => <SessionBars series={s.daily} />,
    },
    { key: 'sessions', header: '세션', align: 'right', className: 'tabular-nums', card: 'line', cell: (s) => fmtNum(s.sessions) },
    { key: 'views', header: '조회', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (s) => fmtNum(s.views) },
    {
      key: 'perSession',
      header: <span title="탭이 앞에 있고 3분 안에 누르거나 스크롤한 시간을 세션 수로 나눈 값">세션당 이용</span>,
      align: 'right',
      className: 'tabular-nums',
      card: 'line',
      cell: (s) => (s.sessions > 0 ? fmtDuration(s.time_ms / s.sessions) : '—'),
    },
    {
      key: 'last',
      header: '마지막 기록',
      align: 'right',
      card: 'line',
      cell: (s) =>
        s.error ? (
          <span className="text-status-failed" title={s.error}>
            읽지 못했다
          </span>
        ) : s.last_event_at ? (
          formatRelative(fromUnixMs(s.last_event_at))
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ]

  return (
    <>
      <PageHeader title="사용량" description="웹 서비스가 Cloudflare에 남기는 이용 기록. 화면별 이용 시간, 누른 기능, 읽은 깊이." docTitle="웹 서비스 사용량">
        <FilterBar
          end={
            buckets.length > 0 && (
              <p className="text-meta text-muted-foreground">
                <span title={rangeTitle(buckets)}>{rangeLabel(buckets)}</span>
              </p>
            )
          }
        >
          <UsageTabs value="web" />
          <WindowPicker days={days} onChange={setDays} />
          <ScopePicker internal={internal} onChange={setInternal} />
        </FilterBar>
      </PageHeader>

      <div className="space-y-6">
        {!configured && (
          <Notice tone="neutral" title="Cloudflare 토큰이 아직 없다">
            <Link to="/settings?tab=webusage" className="text-accent hover:underline">
              설정의 웹 서비스 분석
            </Link>
            에 Account Analytics 읽기 권한 토큰을 넣으면 숫자가 나온다. 그 전에는 Cloudflare로 아무것도 묻지 않는다.
          </Notice>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="서비스" value={items.length} />
          <StatTile label={`세션 · ${windowLabel(days)}`} value={fmtNum(sessions)} />
          <StatTile label="세션당 이용" value={sessions > 0 ? fmtDuration(timeMs / sessions) : '—'} />
          <StatTile label="가장 새 기록" value={last ? formatRelative(fromUnixMs(last)) : '—'} hint="Cloudflare에서 5분마다 새로 읽는다" />
        </div>

        <section aria-label="웹 서비스" className="space-y-2">
          <div className="flex items-baseline gap-x-2">
            <h2 className="text-section font-semibold">웹 서비스</h2>
            <span className="text-meta text-muted-foreground">설정에 켜 둔 서비스가 한 줄</span>
          </div>
          <DataTable
            label="웹 서비스 사용량"
            rows={list.data ? items : undefined}
            rowKey={(s) => s.id}
            columns={columns}
            loading={list.isLoading}
            error={list.error}
            onRetry={() => list.refetch()}
            empty={
              <EmptyState
                title="켜 둔 웹 서비스가 없다"
                description="설정 → 웹 서비스에서 서비스 이름과 Analytics Engine 데이터셋을 더한다."
              />
            }
          />
        </section>

        <Disclosure title="설명">
          <dl className="space-y-3 text-table">
            <div>
              <dt className="font-medium">어디서 오는 숫자인가</dt>
              <dd className="mt-0.5 text-muted-foreground">
                서비스마다 자기 Cloudflare Worker에서 Workers Analytics Engine에 기록하고, 이 화면이 Cloudflare SQL API로 읽는다. 여기에 복사해 두지 않아서 보관은
                Cloudflare의 3개월까지다.
              </dd>
            </div>
            <div>
              <dt className="font-medium">이용 시간</dt>
              <dd className="mt-0.5 text-muted-foreground">
                탭이 화면 앞에 있는 동안만 센다. 마지막으로 누르거나 입력하거나 스크롤한 뒤 3분이 지나면 멈춘다.
              </dd>
            </div>
            <div>
              <dt className="font-medium">기간</dt>
              <dd className="mt-0.5 text-muted-foreground">
                지금부터 거꾸로 기간×24시간이다. 추이 막대는 UTC 날짜(24시간이면 시간)로 나눈다.
              </dd>
            </div>
          </dl>
        </Disclosure>
      </div>
    </>
  )
}
